import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { EmptyState } from "@/components/ui/EmptyState";
import { Field } from "@/components/ui/Field";
import { Textarea } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/useToast";
import { formatRelative } from "@/lib/format";
import { inbound, type InboundPrefill, type InboundRecord } from "@/lib/phase2";
import { strings } from "@/lib/strings";

/**
 * Records sent by the laboratory system, waiting to be checked (IN-08 … IN-13).
 *
 * A record is a suggestion, never an isolate. Opening one pre-fills the entry
 * form; the volunteer checks it against the register and saves. Anything the
 * lab's wording did not match is named explicitly, because a pre-filled form
 * that looks complete but is subtly wrong is worse than an empty one.
 */
export function InboundQueue() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const { isElevated } = useAuth();

  const [records, setRecords] = useState<InboundRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<InboundPrefill | null>(null);
  const [discarding, setDiscarding] = useState<InboundRecord | null>(null);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRecords(await inbound.pending());
    } catch {
      notify(strings.errors.network, "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = async (record: InboundRecord) => {
    try {
      setPreview(await inbound.get(record.id));
    } catch {
      notify(strings.errors.notFound, "error");
    }
  };

  const startEntry = (record: InboundPrefill) => {
    // The pre-fill travels as route state. The record is linked only once the
    // isolate has actually saved, so a failed save cannot consume it (IN-10).
    navigate("/entry", { state: { inbound: record } });
  };

  const discard = async () => {
    if (!discarding || reason.trim().length < 3) return;
    try {
      await inbound.discard(discarding.id, reason.trim());
      notify("Discarded. The record is kept with your reason.", "success");
      setDiscarding(null);
      setReason("");
      await load();
    } catch {
      notify(strings.errors.forbidden, "error");
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">From the laboratory</h1>
        <p className="text-sm text-slate-500">
          Records the lab system sent. Each one still needs checking against the
          register before it becomes an isolate.
        </p>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="p-8">
            <Spinner label="Loading the queue" />
          </div>
        ) : records.length === 0 ? (
          <EmptyState
            title="Nothing waiting."
            description="Records appear here when a laboratory export is loaded."
          />
        ) : (
          <Table caption="Records from the laboratory system">
            <thead>
              <tr>
                <Th>Received</Th>
                <Th>Source</Th>
                <Th>Their reference</Th>
                <Th>
                  <span className="sr-only">Actions</span>
                </Th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr key={record.id} className="hover:bg-slate-50">
                  <Td className="whitespace-nowrap text-xs">
                    {formatRelative(record.created_at)}
                  </Td>
                  <Td>
                    <Badge>{record.source}</Badge>
                  </Td>
                  <Td className="font-mono text-xs">{record.external_id}</Td>
                  <Td className="whitespace-nowrap">
                    <div className="flex gap-2">
                      <Button size="sm" variant="secondary" onClick={() => void open(record)}>
                        Check it
                      </Button>
                      {isElevated && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setDiscarding(record)}
                        >
                          Discard
                        </Button>
                      )}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Dialog
        open={preview !== null}
        onOpenChange={() => setPreview(null)}
        title="What the laboratory sent"
        description="Check this against the register before you save it."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPreview(null)}>
              {strings.common.cancel}
            </Button>
            <Button onClick={() => preview && startEntry(preview)}>
              Use it to start an entry
            </Button>
          </>
        }
      >
        {preview && (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {Object.entries(preview.prefill)
                .filter(([, value]) => value !== null && value !== "")
                .map(([key, value]) => (
                  <div key={key} className="contents">
                    <dt className="text-slate-500">{key.replace(/_/g, " ")}</dt>
                    <dd className="text-slate-900">
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                    </dd>
                  </div>
                ))}
            </dl>

            {preview.unresolved.length > 0 && (
              <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                The laboratory's wording did not match our list for{" "}
                <strong>{preview.unresolved.join(", ").replace(/_/g, " ")}</strong>.
                You will need to choose those yourself.
              </p>
            )}

            {Object.keys(preview.unmapped).length > 0 && (
              <div className="rounded-md bg-slate-50 p-3 text-xs text-slate-600">
                <p className="font-medium">Columns we do not use, kept for reference:</p>
                <ul className="mt-1 space-y-0.5">
                  {Object.entries(preview.unmapped).map(([key, value]) => (
                    <li key={key}>
                      {key}: {String(value)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Dialog>

      <Dialog
        open={discarding !== null}
        onOpenChange={() => setDiscarding(null)}
        title="Discard this record"
        description="Nothing is deleted. The record is kept with your reason."
        footer={
          <>
            <Button variant="secondary" onClick={() => setDiscarding(null)}>
              {strings.common.cancel}
            </Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              onClick={() => void discard()}
            >
              Discard
            </Button>
          </>
        }
      >
        <Field id="discard-reason" label="Why?" required>
          <Textarea
            id="discard-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Duplicate of an export already loaded."
          />
        </Field>
      </Dialog>
    </div>
  );
}
